import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../services/apiService';
import { isQaEnvironment } from '../utils/subdomain';
import './SyncAmbientes.css';

/**
 * Sincronizador — passo 4: revisão do plano.
 *
 * O plano é citável e revisável como um pull request: tem autor, corte declarado, conteúdo
 * explícito e uma URL estável. Dois admins abrindo o mesmo link vêem a mesma seleção, e um
 * plano pode ser montado hoje e aplicado amanhã.
 *
 * Nesta fase a tela é de LEITURA: mostra o que foi aprovado. O dry-run e o apply entram na
 * fatia seguinte, e vão nesta mesma tela — é aqui que a pessoa aprova.
 *
 * Duas coisas que a tela precisa distinguir e que não são cosméticas:
 *  - item escolhido à mão vs. arrastado pelo fecho de dependências (responsabilidades
 *    diferentes na auditoria);
 *  - escopo global, que vale para o cliente inteiro.
 */

const NOME = { prod: 'Produção', qa: 'QA', hmg: 'Homologação' };

/**
 * Por que cada item NÃO foi levado — o motivo que a API deu, item por item.
 *
 * Isto existe porque a versão anterior desta tela mostrava uma NOTA FIXA afirmando que
 * "item recusado é item que mudou na origem depois do corte", para qualquer recusa. Em
 * 04/09/2026 um comportamento foi recusado por outra razão inteiramente — a loja
 * `Loja_Aliado` não existe no destino — e a tela informou a causa errada, mandando montar
 * um plano novo, o que não resolveria nada. A API sabia o motivo certo e a tela jogava
 * fora.
 *
 * Regra que fica: causa de recusa NUNCA é adivinhada na tela. Ou vem do `motivo` da API,
 * ou não é dita.
 */
function PorQue({ entidades }) {
    const linhas = (entidades || []).flatMap((e) =>
        (e.detalhes || [])
            .filter((d) => !['criou', 'versionou', 'atualizou', 'removeu'].includes(d.acao))
            .map((d) => ({ ...d, label: e.label })));
    if (!linhas.length) return null;

    // Conflito de id de pessoa é a única recusa que quem opera NÃO consegue resolver
    // sozinho: alinhar id mexe em 68 colunas de 10 schemas de uma vez. Sobe para um aviso
    // em destaque em vez de ficar numa linha de tabela, senão a pessoa relê o motivo três
    // vezes tentando entender o que fazer.
    const conflitos = linhas.filter((d) => (d.motivo || '').includes('CONFLITO DE ID'));

    const rotulo = {
        recusou: 'não foi levado',
        pulou: 'não precisou',
        nao_encontrado: 'sumiu da origem'
    };

    return (
        <>
        {conflitos.length > 0 && (
            <p className="sync-aviso-alto">
                <strong>{conflitos.length}</strong>{' '}
                {conflitos.length === 1 ? 'item não foi levado por CONFLITO DE ID'
                                        : 'itens não foram levados por CONFLITO DE ID'}
                {' '}de pessoa. Isso <strong>não se resolve por esta tela</strong>: a mesma
                pessoa está com id diferente nos dois ambientes, ou o id que a origem usa
                está ocupado por outra pessoa no destino. Alinhar id de pessoa mexe em 68
                colunas de 10 schemas de uma vez, e é justamente o que impede o atendimento
                de uma pessoa de virar de outra.{' '}
                <strong>Procure a equipe de tecnologia</strong> com os nomes e os ids que
                aparecem no motivo abaixo. Depois de alinhado, monte um plano novo.
            </p>
        )}
        <div className="sync-tabela-scroll" style={{ marginTop: 12 }}>
            <table className="sync-tabela">
                <thead>
                    <tr>
                        <th style={{ width: '9rem' }}>O que houve</th>
                        <th>Item</th>
                        <th>Por quê</th>
                    </tr>
                </thead>
                <tbody>
                    {linhas.map((d, i) => (
                        <tr key={i}>
                            <td>
                                <span className={`sync-badge ${d.acao === 'recusou' ? 'dif' : 'indisp'}`}>
                                    {rotulo[d.acao] || d.acao}
                                </span>
                            </td>
                            <td className="num">
                                {d.chave}
                                {' '}<span className="sync-nota">({d.label})</span>
                            </td>
                            <td>{d.motivo || <em className="sync-nota">a API não informou o motivo</em>}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        </>
    );
}

/**
 * Avisos por item. Não são erros: o item foi gravado. São o que ele NÃO resolve sozinho.
 *
 * O caso que obriga isso a existir: uma atribuição com `prompt_id` preenchido está FIXADA
 * numa versão do prompt. Levar o prompt grava a versão nova, mas a atribuição continua
 * apontando para a anterior — em usaflex as 13 estão todas fixadas. Sem este bloco o
 * sincronizador diria "versionei 6" e nada teria mudado no comportamento do cliente, que é
 * o pior modo de falha possível aqui: silencioso e com cara de sucesso.
 */
function Avisos({ entidades }) {
    const todos = (entidades || []).flatMap((e) =>
        (e.avisos || []).map((a) => ({ ...a, label: e.label })));
    if (!todos.length) return null;
    return (
        <div className="sync-nota destaque">
            <strong>Atenção — o que isto não resolve sozinho:</strong>
            <ul style={{ margin: '8px 0 0', paddingLeft: '1.2rem' }}>
                {todos.map((a, i) => (
                    <li key={i}>
                        <code>{a.chave}</code>{' '}
                        <span className="sync-nota">({a.label})</span> — {a.aviso}
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * Desfazer, na tela onde a pessoa aplicou.
 *
 * Não numa tela de execuções à parte: quem acabou de aplicar e quer voltar atrás está
 * olhando o plano, e mandar procurar o run numa outra lista é onde se erra o run.
 *
 * Duas coisas que a UI é obrigada a dizer, e que não são detalhe:
 *
 *  - o desfazer restaura CONFIGURAÇÃO, nunca CONSEQUÊNCIA. Atendimento já avaliado com o
 *    prompt novo não volta a ser o que era. Sem isso escrito, "desfazer" promete o que não
 *    entrega.
 *  - é TUDO OU NADA. Se uma linha foi mexida por outra pessoa depois do apply, nada é
 *    desfeito e ela é nomeada. É melhor do que reverter metade.
 */
function Desfazer({ plano, runs, onFeito }) {
    const [simulado, setSimulado] = React.useState(null);
    const [feito, setFeito] = React.useState(null);
    const [erro, setErro] = React.useState(null);
    const [rodando, setRodando] = React.useState(null);
    const [confirmando, setConfirmando] = React.useState(false);

    // O que este plano escreveu. Um plano com duas entidades gera DOIS runs (um por
    // entidade), e o desfazer pega todos de uma vez — desfazer um só deixaria o outro
    // apontando para linhas apagadas.
    const aplicados = (runs || []).filter((r) => r.tipo === 'apply' && r.status === 'ok');
    if (!plano || !aplicados.length) return null;

    const jaDesfeito = aplicados.filter((r) => r.desfeito_por);
    const escreveu = aplicados.reduce((acc, r) => {
        const res = r.resumo || {};
        return { criou: acc.criou + (res.criou || 0),
                 versionou: acc.versionou + (res.versionou || 0),
                 atualizou: acc.atualizou + (res.atualizou || 0),
                 removeu: acc.removeu + (res.removeu || 0) };
    }, { criou: 0, versionou: 0, atualizou: 0, removeu: 0 });

    async function rodar(dryRun) {
        setRodando(dryRun ? 'simular' : 'aplicar');
        setErro(null);
        try {
            const r = await apiService.syncDesfazer({
                ambiente: plano.destino, planId: Number(plano.plan_id), dryRun
            });
            if (dryRun) { setSimulado(r); setFeito(null); }
            else {
                setFeito(r);
                setSimulado(null);
                setConfirmando(false);
                if (onFeito) await onFeito();
            }
        } catch (e) {
            setErro(e?.response?.data?.error || e.message);
        } finally {
            setRodando(null);
        }
    }

    return (
        <section className="sync-secao">
            <h2>Desfazer</h2>

            <p className="sync-nota destaque">
                O desfazer restaura <strong>configuração</strong>, nunca{' '}
                <strong>consequência</strong>: atendimento já avaliado com o prompt novo não
                volta atrás. E é <strong>tudo ou nada</strong> — se alguma linha foi alterada
                por outra pessoa depois da aplicação, nada é desfeito e ela é apontada.
            </p>

            {erro && <div className="sync-erro">⚠️ {erro}</div>}

            <div className="sync-resumo">
                <span className="sync-chip dif">
                    aplicado em {NOME[plano.destino] || plano.destino}
                </span>
                {escreveu.criou > 0 && (
                    <span className="sync-chip igual">{escreveu.criou} criados</span>
                )}
                {escreveu.versionou > 0 && (
                    <span className="sync-chip warn">{escreveu.versionou} versionados</span>
                )}
                {escreveu.removeu > 0 && (
                    <span className="sync-chip dif">{escreveu.removeu} removidos</span>
                )}
                {escreveu.atualizou > 0 && (
                    <span className="sync-chip warn">{escreveu.atualizou} atualizados</span>
                )}
                <span className="sync-nota">
                    {aplicados.length === 1 ? 'execução' : 'execuções'}{' '}
                    {aplicados.map((r) => `#${r.run_id}`).join(', ')}
                </span>
            </div>

            {jaDesfeito.length > 0 ? (
                <p className="sync-nota">
                    Este plano <strong>já foi desfeito</strong> —{' '}
                    {jaDesfeito.map((r) => `#${r.run_id} pela #${r.desfeito_por}`).join(', ')}.
                    Para levar de novo, monte um plano novo: reaplicar sem plano é
                    exatamente o que a ferramenta não faz.
                </p>
            ) : (
                <div className="sync-barra">
                    <button type="button" className="sync-botao-linha"
                            disabled={!!rodando}
                            onClick={() => rodar(true)}>
                        {rodando === 'simular' ? 'Simulando…' : 'Simular desfazer'}
                    </button>
                    <span className="sync-nota">
                        Roda o desfazer inteiro, confere cada linha e volta tudo.
                    </span>
                </div>
            )}

            {simulado && (
                <div className="sync-nota destaque" style={{ marginTop: 12 }}>
                    <p style={{ margin: '0 0 8px' }}>
                        Desfazer este plano em{' '}
                        <strong>{NOME[plano.destino] || plano.destino}</strong> vai reverter{' '}
                        <strong>{simulado.resumo.desfez}</strong>{' '}
                        {simulado.resumo.desfez === 1 ? 'item' : 'itens'}
                        {simulado.resumo.pulou > 0 && (
                            <> e pular <strong>{simulado.resumo.pulou}</strong> que já não
                            existem mais</>
                        )}. Nada foi gravado nesta simulação.
                    </p>
                    <PorQueDesfazer detalhes={simulado.detalhes} />
                    {!confirmando ? (
                        <button type="button" className="sync-botao-principal"
                                disabled={!!rodando || simulado.resumo.desfez === 0}
                                onClick={() => setConfirmando(true)}>
                            {simulado.resumo.desfez === 0 ? 'Nada a desfazer'
                                                          : 'Desfazer de verdade →'}
                        </button>
                    ) : (
                        <div className="sync-barra">
                            <button type="button" className="sync-botao-principal"
                                    disabled={!!rodando}
                                    onClick={() => rodar(false)}>
                                {rodando === 'aplicar' ? 'Desfazendo…' : 'Confirmar e desfazer'}
                            </button>
                            <button type="button" className="sync-botao-linha"
                                    onClick={() => setConfirmando(false)}>cancelar</button>
                        </div>
                    )}
                </div>
            )}

            {feito && (
                <div className="sync-nota destaque" style={{ marginTop: 12 }}>
                    <p style={{ margin: '0 0 8px' }}>
                        Plano desfeito — <strong>{feito.resumo.desfez}</strong>{' '}
                        {feito.resumo.desfez === 1 ? 'item revertido' : 'itens revertidos'}
                        {feito.resumo.pulou > 0 && <> · {feito.resumo.pulou} pulados</>}.
                        {(feito.runs_desfeitos || []).length > 0 && (
                            <> Registrado como{' '}
                            {feito.runs_desfeitos.map((x) => `#${x.rollback}`).join(', ')}.</>
                        )}
                    </p>
                    <PorQueDesfazer detalhes={feito.detalhes} />
                </div>
            )}
        </section>
    );
}

/** O que o desfazer fez com cada item — a mesma disciplina do bloco "Por quê" do apply. */
function PorQueDesfazer({ detalhes }) {
    if (!detalhes || !detalhes.length) return null;
    const rotulo = { criou: 'apagou a linha criada',
                     versionou: 'apagou a versão criada e as perguntas dela',
                     atualizou: 'restaurou os campos anteriores',
                     removeu: 'trouxe de volta o que foi removido' };
    return (
        <div className="sync-tabela-scroll">
            <table className="sync-tabela">
                <thead>
                    <tr><th style={{ width: '7rem' }}>Ação</th><th>Item</th><th>O que faz</th></tr>
                </thead>
                <tbody>
                    {detalhes.map((d, i) => (
                        <tr key={i}>
                            <td>
                                <span className={`sync-badge ${d.acao === 'desfez' ? 'igual' : 'indisp'}`}>
                                    {d.acao}
                                </span>
                            </td>
                            <td className="num">
                                {d.chave} <span className="sync-nota">({d.entidade})</span>
                            </td>
                            <td>{d.motivo || rotulo[d.era] || '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function SyncPlano({ user }) {
    const { id } = useParams();
    const ehQa = isQaEnvironment();

    const [dados, setDados] = useState(null);
    const [erro, setErro] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [rodando, setRodando] = useState(null);      // 'simular' | 'aplicar'
    const [simulacao, setSimulacao] = useState(null);
    const [aplicado, setAplicado] = useState(null);
    const [confirmando, setConfirmando] = useState(false);
    const [runs, setRuns] = useState([]);

    const carregarRuns = useCallback(async () => {
        try {
            const h = await apiService.syncRuns({ planId: Number(id) });
            setRuns(h.runs || []);
        } catch (e) {
            // Histórico é secundário: não pode impedir a tela do plano de abrir.
            setRuns([]);
        }
    }, [id]);

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro(null);
        try {
            setDados(await apiService.syncLerPlano(Number(id)));
            await carregarRuns();
        } catch (e) {
            setErro(e?.response?.data?.error || e.message);
        } finally {
            setCarregando(false);
        }
    }, [id, carregarRuns]);

    useEffect(() => {
        if (!ehQa || !user?.can_sync) { setCarregando(false); return; }
        carregar();
    }, [ehQa, user, carregar]);

    if (!ehQa || !user?.can_sync) {
        return (
            <div className="sync-wrap">
                <div className="sync-bloqueio"><h1>Sincronizador de Ambientes</h1>
                    <p>Acesso restrito.</p></div>
            </div>
        );
    }

    async function executar(dryRun) {
        setRodando(dryRun ? 'simular' : 'aplicar');
        setErro(null);
        try {
            const r = await apiService.syncExecutar({ planId: Number(id), dryRun });
            if (dryRun) { setSimulacao(r); setAplicado(null); }
            else { setAplicado(r); setSimulacao(null); setConfirmando(false); await carregar(); }
        } catch (e) {
            setErro(e?.response?.data?.error || e.message);
        } finally {
            setRodando(null);
        }
    }

    // O que conta como ESCRITA. `versionou` é gravação de versão nova numa linhagem
    // existente (prompts é append-only: alinhar não é sobrescrever, é versionar) — deixar
    // fora daqui desabilitaria o botão de aplicar num plano que tem trabalho a fazer.
    const escreve = (r) => (r?.criou || 0) + (r?.versionou || 0) + (r?.atualizou || 0);

    const p = dados?.plano;
    const totalGlobais = (dados?.entidades || [])
        .reduce((acc, g) => acc + (g.escopo_global || 0), 0);
    const totalDependencia = (dados?.entidades || [])
        .reduce((acc, g) => acc + (g.dependencia || 0), 0);
    const ehProducao = p?.destino === 'prod';

    return (
        <div className="sync-wrap">
            <header className="sync-head">
                <a className="sync-voltar" href="/sync">← Sincronizador</a>
                <h1>
                    Plano #{id}
                    {p && <span className="sync-tenant">{p.subdomain}</span>}
                </h1>
                {p && (
                    <>
                        <p className="sync-sub">
                            {NOME[p.origem]} <span className="sync-seta">→</span> {NOME[p.destino]}
                            {' '}· {p.classe === 'config' ? 'configurações' : 'atendimentos'}
                            {' '}· {dados.total_itens} {dados.total_itens === 1 ? 'item' : 'itens'}
                        </p>
                        <p className="sync-corte">
                            corte <strong>{(p.corte || '').replace('T', ' ').slice(0, 19)}</strong>
                            {' '}· montado por <strong>{p.criado_por}</strong>
                            {' '}em {(p.created || '').replace('T', ' ').slice(0, 16)}
                            {' '}· situação <span className={`sync-badge ${p.status === 'aplicado' ? 'igual' : 'warn'}`}>{p.status}</span>
                        </p>
                    </>
                )}
            </header>

            {erro && <div className="sync-erro">⚠️ {erro}</div>}
            {carregando && <div className="sync-info">Carregando o plano…</div>}

            {!carregando && p && (
                <>
                    {ehProducao && (
                        <p className="sync-aviso-alto">
                            O destino é <strong>produção</strong>. Aplicar escreve em dados que os
                            clientes usam. A aplicação tem desfazer, mas o desfazer restaura
                            <strong> configuração, não consequência</strong>: atendimento já
                            avaliado com um prompt novo não volta atrás.
                        </p>
                    )}

                    <div className="sync-resumo">
                        <span className="sync-chip dif">{dados.total_itens} no plano</span>
                        {totalDependencia > 0 && (
                            <span className="sync-chip warn"
                                  title="Itens que entraram porque outro item selecionado depende deles">
                                {totalDependencia} por dependência
                            </span>
                        )}
                        {totalGlobais > 0 && (
                            <span className="sync-chip dif"
                                  title="Escopo global vale para o cliente inteiro">
                                {totalGlobais} de escopo global
                            </span>
                        )}
                    </div>

                    {(dados.entidades || []).map((g) => (
                        <section className="sync-secao" key={g.entidade}>
                            <h2>
                                {g.label}
                                {' '}<span className="sync-nota">
                                    ({g.itens.length} {g.itens.length === 1 ? 'item' : 'itens'}
                                    {g.ausente ? ` · ${g.ausente} a criar` : ''}
                                    {g.divergente ? ` · ${g.divergente} a atualizar` : ''})
                                </span>
                            </h2>
                            <div className="sync-tabela-scroll">
                                <table className="sync-tabela">
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th>O que vai acontecer</th>
                                            <th>Escopo</th>
                                            <th>Entrou</th>
                                            <th>Chave</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {g.itens.map((i) => (
                                            <tr key={i.chave}>
                                                <td>{i.rotulo || <em>(sem rótulo)</em>}</td>
                                                <td>
                                                    <span className={`sync-badge ${i.estado === 'ausente' ? 'igual' : 'warn'}`}>
                                                        {i.estado === 'ausente' ? 'criar no destino'
                                                                                : 'atualizar o existente'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`sync-badge escopo-${i.escopo}`}>
                                                        {i.escopo === 'global' ? 'GLOBAL' : (i.escopo || '—')}
                                                    </span>
                                                </td>
                                                <td>
                                                    {i.selecao === 'manual'
                                                        ? <span className="sync-nota">escolhido</span>
                                                        : <span className="sync-badge warn">dependência</span>}
                                                </td>
                                                <td className="num">{i.chave}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    ))}

                    <div className="sync-barra">
                        <button type="button" className="sync-botao-principal"
                                disabled={!!rodando}
                                onClick={() => executar(true)}>
                            {rodando === 'simular' ? 'Simulando…' : 'Simular aplicação'}
                        </button>
                        <span className="sync-nota">
                            Roda o apply inteiro no destino e desfaz com <code>ROLLBACK</code>.
                            É o mesmo código do apply, só o desfecho muda — por isso o resultado
                            não pode divergir do que aconteceria de verdade.
                        </span>
                    </div>

                    {simulacao && (
                        <section className="sync-secao">
                            <h2>Resultado da simulação</h2>
                            <div className="sync-resumo">
                                {simulacao.resumo.criou > 0 && (
                                    <span className="sync-chip igual">{simulacao.resumo.criou} a criar</span>
                                )}
                                {simulacao.resumo.versionou > 0 && (
                                    <span className="sync-chip warn"
                                          title="Grava uma versão nova na linhagem existente, sem apagar a anterior">
                                        {simulacao.resumo.versionou} a versionar
                                    </span>
                                )}
                                {simulacao.resumo.atualizou > 0 && (
                                    <span className="sync-chip warn"
                                          title="Sobrescreve linhas que já existem no destino">
                                        {simulacao.resumo.atualizou} a atualizar
                                    </span>
                                )}
                                {simulacao.resumo.pulou > 0 && (
                                    <span className="sync-chip indisp"
                                          title="Já existem no destino, pela chave de negócio">
                                        {simulacao.resumo.pulou} já existem
                                    </span>
                                )}
                                {simulacao.resumo.recusou > 0 && (
                                    <span className="sync-chip dif"
                                          title="Mudaram na origem depois do corte">
                                        {simulacao.resumo.recusou} recusados
                                    </span>
                                )}
                                {simulacao.resumo.nao_encontrado > 0 && (
                                    <span className="sync-chip warn">
                                        {simulacao.resumo.nao_encontrado} não existem mais na origem
                                    </span>
                                )}
                            </div>
                            <PorQue entidades={simulacao.entidades} />
                            <Avisos entidades={simulacao.entidades} />
                            <p className="sync-nota">Nada foi gravado nesta simulação.</p>

                            {!confirmando ? (
                                <button type="button" className="sync-botao-principal"
                                        disabled={!!rodando || escreve(simulacao.resumo) === 0}
                                        onClick={() => setConfirmando(true)}>
                                    {escreve(simulacao.resumo) === 0
                                        ? 'Nada a aplicar'
                                        : `Aplicar de verdade em ${NOME[p.destino]} →`}
                                </button>
                            ) : (
                                <div className={ehProducao ? 'sync-aviso-alto' : 'sync-nota destaque'}>
                                    <p style={{ margin: '0 0 10px' }}>
                                        Vai criar <strong>{simulacao.resumo.criou}</strong>,{' '}
                                        <strong>versionar {simulacao.resumo.versionou || 0}</strong> e{' '}
                                        <strong>sobrescrever {simulacao.resumo.atualizou}</strong> em{' '}
                                        <strong>{NOME[p.destino]}</strong>
                                        {ehProducao && ' — que é o ambiente que os clientes usam'}.
                                        {totalGlobais > 0 && ` Inclui ${totalGlobais} de escopo global.`}
                                    </p>
                                    <div className="sync-barra">
                                        <button type="button" className="sync-botao-principal"
                                                disabled={!!rodando}
                                                onClick={() => executar(false)}>
                                            {rodando === 'aplicar' ? 'Aplicando…' : 'Confirmar e aplicar'}
                                        </button>
                                        <button type="button" className="sync-botao-linha"
                                                onClick={() => setConfirmando(false)}>cancelar</button>
                                    </div>
                                </div>
                            )}
                        </section>
                    )}

                    {aplicado && (
                        <section className="sync-secao">
                            <h2>Aplicado</h2>
                            <div className="sync-resumo">
                                <span className="sync-chip igual">{aplicado.resumo.criou} criados</span>
                                {aplicado.resumo.versionou > 0 && (
                                    <span className="sync-chip warn">{aplicado.resumo.versionou} versionados</span>
                                )}
                                {aplicado.resumo.atualizou > 0 && (
                                    <span className="sync-chip warn">{aplicado.resumo.atualizou} atualizados</span>
                                )}
                                {aplicado.resumo.pulou > 0 && (
                                    <span className="sync-chip indisp">{aplicado.resumo.pulou} já existiam</span>
                                )}
                                {aplicado.resumo.recusou > 0 && (
                                    <span className="sync-chip dif">{aplicado.resumo.recusou} recusados</span>
                                )}
                            </div>
                            <PorQue entidades={aplicado.entidades} />
                            <Avisos entidades={aplicado.entidades} />
                            <p className="sync-nota">
                                Registrado em {NOME[aplicado.destino]}
                                {aplicado.entidades.map((e) => e.run_id).filter(Boolean).length > 0 && (
                                    <> · execução{' '}
                                    {aplicado.entidades.map((e) => e.run_id).filter(Boolean).join(', ')}</>
                                )}. O desfazer entra na Fase 4.
                            </p>
                        </section>
                    )}

                    <Desfazer plano={p} runs={runs} onFeito={carregar} />
                </>
            )}
        </div>
    );
}
